import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { buttonClassName, cn, VisuallyHidden } from '@ircf/ui';
import { pathwayFor, pathwayForPath, primaryNav, publicRoutes } from '@ircf/config';
import { useSession } from '../../app/session.js';
import { buttonPathway } from '../../lib/pathway.js';
import { BrandLockup } from './BrandLockup.js';

/**
 * Public site header.
 *
 * Three requirements from the brief drive the design:
 *
 *  1. The visitor must always know which of the three experiences they are in.
 *     The active pathway drives an accent colour and a wordmark suffix - `WITH
 *     PURPOSE`, `BY DESIGN`, `IN ROUNDS` - while the word `BUILT` stays fixed.
 *  2. Book must be visible on desktop *and* mobile. It sits outside the
 *     collapsible menu, so it is reachable without opening anything.
 *  3. Navigation is real HTML. It works with the 3D canvas absent, broken or
 *     disabled, and it never depends on hover.
 */

const NAV_LINKS = primaryNav.filter(
  (route) => route.id !== publicRoutes.book.id && route.id !== publicRoutes.login.id,
);

export function PublicHeader() {
  const location = useLocation();
  const { isAuthenticated, role } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);

  const identity = pathwayFor(pathwayForPath(location.pathname));

  // Close the menu on navigation, otherwise it stays open over the new page.
  // Adjusted during render rather than in an effect, so the new route is never
  // painted with the previous page's menu covering it.
  const [renderedPath, setRenderedPath] = useState(location.pathname);
  if (renderedPath !== location.pathname) {
    setRenderedPath(location.pathname);
    setMenuOpen(false);
  }

  // Solid background once scrolled, so the transparent hero treatment does not
  // leave navigation unreadable over bright scene content.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const portalHref = role === 'client' ? '/portal/dashboard' : '/admin/dashboard';

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
        scrolled || menuOpen
          ? 'border-b border-white/10 bg-near-black/95 backdrop-blur-md'
          : 'bg-gradient-to-b from-near-black/80 to-transparent',
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:h-20 sm:px-6 lg:px-8">
        {/* Business name is the primary mark; BUILT WITH PURPOSE (or the
            pathway qualifier) sits underneath. */}
        <BrandLockup />

        <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((route) => (
            <NavLink
              key={route.id}
              to={route.path}
              end={route.path === '/'}
              className={({ isActive }) =>
                cn(
                  'rounded-full px-3 py-2 text-sm font-medium transition-colors',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-warm-white',
                  isActive
                    ? 'bg-white/10 text-warm-white'
                    : 'text-platinum hover:bg-white/5 hover:text-warm-white',
                )
              }
            >
              {route.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {isAuthenticated ? (
            <Link
              to={portalHref}
              className={cn(buttonClassName({ variant: 'ghost', size: 'sm' }), 'hidden sm:inline-flex')}
            >
              My Portal
            </Link>
          ) : (
            <Link
              to={publicRoutes.login.path}
              className={cn(buttonClassName({ variant: 'ghost', size: 'sm' }), 'hidden sm:inline-flex')}
            >
              Client Login
            </Link>
          )}

          {/* Always visible, on every breakpoint, per the brief. */}
          <Link
            to={publicRoutes.book.path}
            className={buttonClassName({
              variant: 'primary',
              size: 'sm',
              pathway: buttonPathway(identity.id),
            })}
          >
            Book
          </Link>

          <button
            ref={toggleRef}
            type="button"
            className="flex size-10 items-center justify-center rounded-full text-platinum transition-colors hover:bg-white/10 hover:text-warm-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-warm-white lg:hidden"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <VisuallyHidden>{menuOpen ? 'Close menu' : 'Open menu'}</VisuallyHidden>
            <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5" fill="none">
              {menuOpen ? (
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              ) : (
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu. Rendered conditionally rather than hidden, so its links are
          not reachable by keyboard while collapsed. */}
      {menuOpen ? (
        <div id={menuId} className="border-t border-white/10 bg-near-black lg:hidden">
          <nav aria-label="Primary" className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
            <ul className="flex flex-col gap-1">
              {NAV_LINKS.map((route) => (
                <li key={route.id}>
                  <NavLink
                    to={route.path}
                    end={route.path === '/'}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-11 items-center rounded-lg px-3 text-base font-medium transition-colors',
                        isActive
                          ? 'bg-white/10 text-warm-white'
                          : 'text-platinum hover:bg-white/5 hover:text-warm-white',
                      )
                    }
                  >
                    {route.label}
                  </NavLink>
                </li>
              ))}
              <li className="mt-2 border-t border-white/10 pt-3">
                <Link
                  to={isAuthenticated ? portalHref : publicRoutes.login.path}
                  className="flex min-h-11 items-center rounded-lg px-3 text-base font-medium text-platinum hover:bg-white/5 hover:text-warm-white"
                >
                  {isAuthenticated ? 'My Portal' : 'Client Login'}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      ) : null}
    </header>
  );
}
